/**
 * useDragToLink - Hook for managing drag-to-link state
 *
 * Features:
 * - Track drag source (entity being dragged)
 * - Track current cursor position for preview line
 * - Validate drop targets (must be opposite column, not already approved)
 * - Return link result on successful drop
 */

import { useCallback, useState } from 'react';

import type { FinchEntity } from '@/services/api/entitySyncService';
import type { DetailedFilevineCollectionItemSchema } from '@/services/api/generated/entitySyncService';

import type {
  DragLinkResult,
  DragState,
  Point,
  UseDragToLinkReturn,
} from '../types/linking-view.types';

interface UseDragToLinkProps {
  finchEntities: FinchEntity[];
  filevineEntities: DetailedFilevineCollectionItemSchema[];
}

const initialDragState: DragState = {
  isDragging: false,
  source: null,
  currentPosition: null,
  validDropTarget: null,
};

/**
 * Hook for drag-to-link functionality
 */
export function useDragToLink({
  finchEntities,
  filevineEntities,
}: UseDragToLinkProps): UseDragToLinkReturn {
  const [dragState, setDragState] = useState<DragState>(initialDragState);

  // Start dragging from an entity
  const startDrag = useCallback(
    (entityId: string, entityType: 'finch' | 'filevine', position: Point) => {
      setDragState({
        isDragging: true,
        source: {
          entityId,
          entityType,
          position,
        },
        currentPosition: position,
        validDropTarget: null,
      });
    },
    [],
  );

  // Update cursor position during drag
  const updateDragPosition = useCallback((position: Point) => {
    setDragState((prev) => ({
      ...prev,
      currentPosition: position,
    }));
  }, []);

  // Set valid drop target when hovering
  const setValidDropTarget = useCallback((entityId: string | null) => {
    setDragState((prev) => ({
      ...prev,
      validDropTarget: entityId,
    }));
  }, []);

  // End drag and potentially create link
  const endDrag = useCallback((): DragLinkResult | null => {
    const { source, validDropTarget } = dragState;

    // Reset state
    setDragState(initialDragState);

    // If no valid drop, return null
    if (!source || !validDropTarget) {
      return null;
    }

    // Determine which entity is Finch and which is Filevine
    let finchEntityId: string;
    let filevineEntityId: string;

    if (source.entityType === 'finch') {
      finchEntityId = source.entityId;
      filevineEntityId = validDropTarget;

      // Validate the target exists
      const targetExists = filevineEntities.some(
        (e) => e.id === filevineEntityId,
      );
      if (!targetExists) return null;
    } else {
      filevineEntityId = source.entityId;
      finchEntityId = validDropTarget;

      // Validate the target exists
      const targetExists = finchEntities.some((e) => e.id === finchEntityId);
      if (!targetExists) return null;
    }

    return {
      finchEntityId,
      filevineEntityId,
    };
  }, [dragState, finchEntities, filevineEntities]);

  // Cancel drag without creating link
  const cancelDrag = useCallback(() => {
    setDragState(initialDragState);
  }, []);

  return {
    dragState,
    startDrag,
    updateDragPosition,
    endDrag,
    cancelDrag,
    setValidDropTarget,
  };
}

export default useDragToLink;
